import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
} from "@mui/material";
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  ArrowForward,
} from "@mui/icons-material";
import Loader from "../Loader";

export default function RecentSitesTable({ sites, loading, error }) {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2.5,
        flexGrow: 1,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Your Sites
        </Typography>
        <Button
          size="small"
          endIcon={<ArrowForward />}
          onClick={() => navigate("/dashboard/sites")}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          View All
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Site Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Environment</TableCell>
              <TableCell>Last Backup</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Loader size={24} />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="error">
                    Failed to load sites. {error?.message || ""}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : sites?.length > 0 ? (
              sites.slice(0, 5).map((site) => (
                <TableRow
                  key={site.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/dashboard/sites/${site.id}`)}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: "primary.light",
                          fontSize: "0.875rem",
                        }}
                      >
                        {site.name[0].toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" fontWeight={500}>
                        {site.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={
                        site.status === "ACTIVE" ? (
                          <CheckCircle
                            sx={{ fontSize: 18, color: "success.main" }}
                          />
                        ) : site.status === "WARNING" ? (
                          <Warning
                            sx={{ fontSize: 18, color: "warning.main" }}
                          />
                        ) : (
                          <ErrorIcon
                            sx={{ fontSize: 18, color: "error.main" }}
                          />
                        )
                      }
                      label={site.status.toLowerCase()}
                      size="small"
                      color={
                        site.status === "ACTIVE"
                          ? "success"
                          : site.status === "WARNING"
                            ? "warning"
                            : "error"
                      }
                      sx={{ textTransform: "capitalize" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={site.environment || "Sandbox"}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: "capitalize", fontSize: "0.75rem" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {site.lastBackupAt
                        ? new Date(site.lastBackupAt).toLocaleString()
                        : "No backups"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary">
                    No sites found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
